import {
  QueryObserverResult,
  RefetchOptions,
  RefetchQueryFilters,
  useMutation,
  useQuery,
  useQueryClient,
} from "react-query";
import {
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { addBreadcrumb, setTag } from "@sentry/react";
import completeOnboardingCall, { Response, SuccessResponse } from "utilities/completeOnboardingCall";
import createLightspeedWebhookUrl, { LightspeedWebhookUrlResponse } from "utilities/createLightspeedWebhookUrl";
import getCurrentLocationConfig, {
  LocationConfigsResponse,
} from "utilities/getCurrentLocationConfig";
import getLastCompleteOnboardingRequest, {
  GetLastOnboardingCallResponse,
} from "utilities/getLastCompleteOnboardingRequest";
import getLocationDetails, {
  LocationDetailsProps,
} from "utilities/getLocationDetails";
import getLocations, { Location } from "utilities/getLocations";
import getOAuthTokenRequest, {
  GetOAuthTokenResponse,
} from "utilities/getOAuthToken";
import getOnboardingAuthUrlRequest, {
  GetOnboardingAuthUrlResponse,
} from "utilities/getOnboardingAuthUrl";
import updateLocationConfig, {
  UpdateLocationConfigProps as NewConfigProps,
} from "utilities/updateLocationConfig";

import createLocation from "utilities/createLocation";
import getFormattedDate from "utilities/getFormattedDate";
import getLocationLatestUpdate from "utilities/getLocationLatestUpdate";
import { queryKeys } from "utilities/constants";
import updateLocation from "utilities/updateLocation";
import useApi from "hooks/useApi";
import { useNotifications } from "./NotificationsContext";
import { useParams } from "react-router-dom";

interface UpdateLocationConfigsProps extends NewConfigProps {
  partnerId?: string;
}

export interface LocationContextProps {
  currentLocation?: Location;
  locations?: Location[] | null;
  initialLocations: Location[] | null | undefined;
  isLocationsError: boolean;
  locationsError: Error | null;
  isLocationsLoading: boolean;
  partnerId?: string;
  partnerKey?: string;
  locationActions: {
    create: ({
      locationId,
      name,
      posType,
      partnerKey,
      upserveUsername,
      upservePassword,
    }: CreateLocationProps) => void;
    isCreateLoading: boolean;
    update: ({
      newEnabledState,
      newPartnerVenueId,
      newLocationName
    }: UpdateLocationProps) => void;
    updateConfigs: ({
      id,
      locationId,
      name,
      partnerId,
      posType,
      configs,
    }: UpdateLocationConfigsProps) => void;
    isUpdateLoading: boolean;
    updateSuccess: boolean;
  };
  locationDetails?: LocationDetailsProps | null;
  isLocationDetailsError: boolean;
  isLocationDetailsLoading: boolean;
  locationDetailsError: Error | null;
  refetchLocationDetails?: <TPageData>(
    options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined
  ) => Promise<QueryObserverResult<LocationDetailsProps | null, Error>>;
  configs: LocationConfigsResponse | undefined;
  isLoadingConfigs: boolean;
  refetchLocationConfigs: <TPageData>(
    options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined
  ) => Promise<QueryObserverResult<LocationConfigsResponse | undefined, Error>>;
  sendCompleteOnboardingCallRequest: ({
    id,
    onboardingCode
  }: CompleteOnboardingCallProps) => void;
  lastOnboardingCallRequest: GetLastOnboardingCallResponse | undefined;
  onboardingAuthUrlRequest: GetOnboardingAuthUrlResponse | undefined;
  oAuthTokenRequest: GetOAuthTokenResponse | undefined;
  refetchLocations: <TPageData>(options?: ((RefetchOptions & RefetchQueryFilters<TPageData>) | undefined))
      => Promise<QueryObserverResult<Location[] | undefined, Error>>
  filteredLocations?: Location[] | null;
  setFilteredLocations: React.Dispatch<React.SetStateAction<Location[] | undefined>>;
}

interface CreateLocationProps {
  locationId: string | undefined;
  name: string;
  partnerId: string;
  partnerKey: string;
  posType: string;
  apiType: string | undefined;
  upservePassword?: string;
  upserveUsername?: string;
}

interface UpdateLocationProps {
  newLocationName?: string;
  newEnabledState?: boolean;
  newGDAState?: boolean;
  newPartnerVenueId?: string;
}

interface CompleteOnboardingCallProps {
  id?: number;
  onboardingCode: string;
}

export const LocationContext = createContext<LocationContextProps | null>(null);

LocationContext.displayName = "LocationContext";

export function LocationProvider(props: any): ReactElement {
  const [locations, setLocations] = useState<Location[] | undefined>(undefined);
  const [currentLocation, setCurrentLocation] = useState<Location | undefined>(
    undefined
  );
  const [isLocationsLoading, setIsLocationsLoading] = useState(false);
  const [locationLastUpdate, setLocationLastUpdate] = useState<string | undefined>();

  const [filteredLocations, setFilteredLocations] = useState(locations);

  const { partnerId, posGUID } = useParams();

  const { notify } = useNotifications();

  const queryClient = useQueryClient();

  const apiClient = useApi();

  // const { lastSaved, fetchLastSaved } = useLocationLastSaved();

  // find and set currentLocation State
  const findCurrentLocation = useCallback((locationsData : Location[] | undefined) => {
    const partnerLocation = locationsData?.filter(
      (location) => location.locationId === posGUID
    )[0];

    setCurrentLocation(partnerLocation);
  }, [posGUID]);

  // Get Locations
  const {
    isFetching: isLoading,
    isError: isLocationsError,
    error: locationsError,
    data: initialLocations,
    refetch: refetchLocations,
  } = useQuery<Location[] | undefined, Error>(
    queryKeys.locations,
    async () => await getLocations({ partnerId, apiClient }),
    {
      enabled: !!partnerId,
      onSuccess: (data) => {
        findCurrentLocation(data);
      },
    }
  );

  const resolvedLocationId = currentLocation?.posType !== "non-pos" ? currentLocation?.id : currentLocation?.locationId;

  // Get Location Details
  const getCurrentLocationDetails = useCallback(async () => {
    return await getLocationDetails({
      id: resolvedLocationId,
      partnerId,
      posType: currentLocation?.posType,
      apiClient,
    });
  }, [apiClient, currentLocation?.posType, resolvedLocationId, partnerId]);

  // Get Location latest update
  const getCurrentLocationLatestUpdate = useCallback(async (id:string) => {
    const res = await getLocationLatestUpdate({
      id,
      partnerId,
      apiClient,
    });

    return res;
  }, [apiClient, partnerId]);

  const fetchLastSaved = useCallback(
    async (locationId: string) => {
      try {
        const res = await getCurrentLocationLatestUpdate(locationId);

        if (res?.resource.created_at) {
          const date = getFormattedDate(res.resource.created_at, {
            dateStyle: "medium",
            timeStyle: "short",
          });

          setLocationLastUpdate(date);
        }

      } catch (error) {
        console.error("Error fetching latest update:", error);
      }
    },
    [getCurrentLocationLatestUpdate]
  );

  const {
    data: locationDetails,
    isFetching: isLocationDetailsLoading,
    isError: isLocationDetailsError,
    error: locationDetailsError,
    refetch: refetchLocationDetails,
  } = useQuery<LocationDetailsProps | null, Error>(
    queryKeys.locationDetails,
    getCurrentLocationDetails,
    {
      enabled: !!posGUID,
      refetchOnWindowFocus: false,
      onSuccess: () => {
        if (currentLocation?.locationId) {
          void fetchLastSaved(currentLocation.locationId);
        }
      },
    },
  );

  const {
    isFetching: isLoadingConfigs,
    data: configs,
    refetch: refetchLocationConfigs,
  } = useQuery<LocationConfigsResponse | undefined, Error>(
    queryKeys.locationConfigs,
    async () =>
      await getCurrentLocationConfig({
        id: currentLocation?.id,
        partnerId,
        posType: currentLocation?.posType,
        apiClient,
      }),
    {
      enabled: !!posGUID,
    }
  );

  const {
    data: lastOnboardingCallRequest,
    refetch: refetchLastOnboardingCallRequest,
  } = useQuery<GetLastOnboardingCallResponse | undefined, Error>(
    queryKeys.lastOnboardingRequest,
    async () =>
      await getLastCompleteOnboardingRequest({
        locationId: currentLocation?.locationId,
        partnerId,
        apiType: currentLocation?.apiType,
        apiClient
      }),
    {
      enabled: (currentLocation?.posType === "lightspeed" && currentLocation?.apiType === "lightspeed-api-k-series") || currentLocation?.posType === "shift4",
    }
  );

  const {
    data: onboardingAuthUrlRequest,
    refetch: refetchOnboardingAuthUrl,
  } = useQuery<GetOnboardingAuthUrlResponse | undefined, Error>(
    queryKeys.onboardingAuthUrl,
    async () =>
      await getOnboardingAuthUrlRequest({
        locationId: currentLocation?.locationId,
        partnerId,
        apiClient,
        posType : currentLocation?.posType,
      }),
    {
      enabled: ["lightspeed", "shift4", "square"].includes(currentLocation?.posType ?? ""),
    }
  );

  const {
    data: oAuthTokenRequest,
    refetch: refetchOAuthToken,
  } = useQuery<GetOAuthTokenResponse | undefined, Error>(
    queryKeys.oAuthToken,
      async () =>
        await getOAuthTokenRequest({
          locationId: currentLocation?.locationId,
          partnerId,
          apiClient,
          posType : currentLocation?.posType,
        }),
      {
        enabled: ["square"].includes(currentLocation?.posType ?? ""),
      }
  );


  useEffect(() => {
      setFilteredLocations(initialLocations);
  }, [initialLocations]);

  useEffect(() => {
    if (currentLocation?.id) {
      queryClient.setQueryData(queryKeys.locationDetails, undefined);
      void refetchLocationDetails();

      queryClient.setQueryData(queryKeys.locationConfigs, undefined);
      void refetchLocationConfigs();

      queryClient.setQueryData(queryKeys.lastOnboardingRequest, undefined);
      void refetchLastOnboardingCallRequest();

      queryClient.setQueryData(queryKeys.onboardingAuthUrl, undefined);
      void refetchOnboardingAuthUrl();

      queryClient.setQueryData(queryKeys.oAuthToken, undefined);
      void refetchOAuthToken();
    }
  }, [currentLocation?.id, queryClient, refetchLocationConfigs, refetchLocationDetails, refetchLastOnboardingCallRequest, refetchOnboardingAuthUrl, refetchOAuthToken]);

  useEffect(() => {
    if (initialLocations && locations !== initialLocations ) {
      setLocations(initialLocations);
    }
  }, [initialLocations, locations]);

  useEffect(() => {
    if (locations) {
      const partnerLocation = locations.filter(
        (location) => location.locationId === posGUID
      )[0];

      setCurrentLocation(partnerLocation);
      setTag("locationId", posGUID);
    }
  }, [locations, posGUID]);

  useEffect(() => {
    if (partnerId && !posGUID) {
      queryClient.setQueryData(queryKeys.locations, undefined);
      setLocations(undefined);
      void refetchLocations();
    }
  }, [partnerId, posGUID, queryClient, refetchLocations]);

  useEffect(() => {
    if (isLoading) {
      setIsLocationsLoading(true);
    } else {
      setIsLocationsLoading(false);
    }
  }, [isLoading]);

  const handleCurrentLocationLastUpdate = useCallback(
    () => {
      if (locationLastUpdate && currentLocation) {
        setCurrentLocation({
          ...currentLocation,
          lastUpdate: locationLastUpdate,
        });
      }
    },
    // 🚨 don't add currentLocation to the array to avoid looping issues
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locationLastUpdate],
  );


  useEffect(() => {
    handleCurrentLocationLastUpdate();
  }, [handleCurrentLocationLastUpdate]);


  const createLightspeedWebhookUrlMutation = useMutation(
    async ({
      locationId: newLocationId,
      partnerKey: partnerKeyForNewLocation,
      apiType: apiTypeForNewLocation,
      partnerId: partnerIdForNewLocation,
    }: {
      locationId?: string;
      partnerKey: string;
      apiType?: string;
      partnerId: string;
    }) => {
      addBreadcrumb({
        level: "info",
        category: "user_action",
        message: `Create new Location for partnerId:${partnerId}`,
      });

      const response = await createLightspeedWebhookUrl(
        {
          locationId: newLocationId,
          partnerKey: partnerKeyForNewLocation,
          apiType: apiTypeForNewLocation,
          enabled: true,
          comment: "test"
        },
        apiClient,
        partnerIdForNewLocation
      );

      return response;
    },
    {
      onSuccess: async (body: LightspeedWebhookUrlResponse) => {

        if (!body.hasErrors) {
          notify({
            type: "success",
            message: "Lightspeed Webhook Url created successfully!",
          });
        }
      },
      onError: (error) => {
        notify({
          type: "error",
          message:
            "There was an error creating lightspeed webhook url. Please try again.",
        });
        console.error("There was an error creating the location: ", error);
      },
    }
  );

  const createLocationMutation = useMutation(
    async ({
      locationId: newLocationId,
      name: newLocationName,
      partnerKey: partnerKeyForNewLocation,
      partnerId: partnerIdForNewLocation,
      posType: posTypeForNewLocation,
      apiType: apiTypeForNewLocation,
      upserveUsername: upserveUsernameForNewLocation,
      upservePassword: upservePasswordForNewLocation,
    }: {
      locationId?: string;
      name: string;
      posType: string;
      apiType?: string;
      partnerKey: string;
      partnerId: string;
      upserveUsername?: string;
      upservePassword?: string;
    }) => {
      setIsLocationsLoading(true);

      addBreadcrumb({
        level: "info",
        category: "user_action",
        message: `Create new Location for partnerId:${partnerId}`,
      });

      const response = await createLocation(
        {
          checkMatchEnabled: false,
          enabled: true,
          locationId: newLocationId,
          logWebhook: false,
          name: newLocationName,
          partnerKey: partnerKeyForNewLocation,
          posType: posTypeForNewLocation,
          apiType: apiTypeForNewLocation,
          skipSend: false,
          tableStatusEnabled: false,
          upserveUsername: upserveUsernameForNewLocation,
          upservePassword: upservePasswordForNewLocation,
        },
        apiClient,
        partnerIdForNewLocation
      );

      return response;
    },
    {
      onSuccess: async () => {
        await refetchLocations();
        notify({
          type: "success",
          message: "Location created successfully!",
        });
      },
      onError: (error) => {
        notify({
          type: "error",
          message:
            "There was an error creating the location. Please try again.",
        });
        console.error("There was an error creating the location: ", error);
      },
    }
  );

  const handleCreateLocation = useCallback(
    ({
      locationId: newLocationId,
      name: newLocationName,
      posType: posTypeForNewLocation,
      apiType: apiTypeForNewLocation,
      partnerId: partnerIdForNewLocation,
      partnerKey: partnerKeyForNewLocation,
      upserveUsername: upserveUsernameForNewLocation,
      upservePassword: upservePasswordForNewLocation,
    }: CreateLocationProps) => {
      createLocationMutation.mutate({
        locationId: newLocationId,
        name: newLocationName,
        posType: posTypeForNewLocation,
        apiType: apiTypeForNewLocation,
        partnerId: partnerIdForNewLocation,
        partnerKey: partnerKeyForNewLocation,
        upserveUsername: upserveUsernameForNewLocation,
        upservePassword: upservePasswordForNewLocation,
      });

      if (apiTypeForNewLocation === "lightspeed-api-l-series") {
        createLightspeedWebhookUrlMutation.mutate({
          locationId: newLocationId,
          partnerKey: partnerKeyForNewLocation,
          apiType: apiTypeForNewLocation,
          partnerId: partnerIdForNewLocation,
        });
      }

    },
    [
      createLocationMutation,
      createLightspeedWebhookUrlMutation
    ]
  );

  const updateLocationMutation = useMutation(
    async ({
      newLocationName,
      newEnabledState,
      newPartnerVenueId,
    }: UpdateLocationProps) => {
      addBreadcrumb({
        level: "info",
        category: "user_action",
        message: `Update Location:${currentLocation?.id}`,
      });

      const response = await updateLocation(
        {
          id: resolvedLocationId,
          locationId: currentLocation?.locationId,
          name: newLocationName ?? currentLocation?.name,
          enabled: newEnabledState ?? currentLocation?.enabled,
          partnerVenueId: newPartnerVenueId ?? currentLocation?.partnerVenueId,
          tableStatusEnabled: locationDetails?.tableStatusEnabled,
          checkMatchEnabled: locationDetails?.checkMatchEnabled,
          partnerId,
          posType: currentLocation?.posType ?? "",
        },
        apiClient
      );

      return response;
    },
    {
      onSuccess: async () => {

        await refetchLocations();
        await refetchLocationDetails();
        // refetchLastSaved();


        notify({
          type: "success",
          message: "Location updated successfully!",
        });

        setTimeout(() => {
          updateLocationMutation.reset();
        }, 1000);
      },
      onError: (error) => {
        notify({
          type: "error",
          message:
            "There was an error updating the location. Please try again.",
        });
        console.error("There was an error updating the location: ", error);
        updateLocationMutation.reset();
      },
    }
  );

  const handleUpdateLocation = useCallback(
    ({ newLocationName, newEnabledState, newPartnerVenueId }: UpdateLocationProps) => {
      updateLocationMutation.mutate({
        newLocationName,
        newEnabledState,
        newPartnerVenueId,
      });
    },
    [updateLocationMutation]
  );

  const updateLocationConfigsMutation = useMutation(
    async ({
      id,
      locationId,
      name,
      partnerId,
      posType,
      configs,
    }: UpdateLocationConfigsProps) => {
      addBreadcrumb({
        level: "info",
        category: "user_action",
        message: `Update Location Configuration for locationId:${currentLocation?.id}`,
      });
      const response = await updateLocationConfig(
        {
          id,
          locationId,
          name,
          posType,
          configs,
        },
        apiClient,
        partnerId
      );

      return response;
    },
    {
      onSuccess: async () => {
        await refetchLocations();
        notify({
          type: "success",
          message: "Location Config updated successfully!",
        });
      },
      onError: (error) => {
        notify({
          type: "error",
          message:
            "There was an error updating the location. Please try again.",
        });
        console.error("There was an error updating the location: ", error);
      },
    }
  );

  const handleUpdateLocationConfigs = useCallback(
    ({
      id,
      locationId,
      name,
      partnerId,
      posType,
      configs,
    }: UpdateLocationConfigsProps) => {
      updateLocationConfigsMutation.mutate({
        id,
        locationId,
        name,
        partnerId,
        posType,
        configs,
      });
    },
    [updateLocationConfigsMutation]
  );

  const sendCompleteOnboardingCallRequestMutation = useMutation(
    async ({
      id,
      onboardingCode
    }: CompleteOnboardingCallProps) => {
      addBreadcrumb({
        level: "info",
        category: "user_action",
        message: `Send complete onboarding call request for location :${currentLocation?.id}`,
      });

      const posType = currentLocation?.posType;

      const response = await completeOnboardingCall(
        {
          onboardingCode,
          id,
          partnerId,
          posType,
          apiClient
        }
      );

      return response;
    },
    {
      onSuccess: async (body: Response) => {
        await refetchLocations();
        const successResponse = body as SuccessResponse;

        if (!successResponse.hasErrors) {
          notify({
            type: "success",
            message: "Success complete onboarding call!",
          });
        }
      },
      onError: (error) => {
        notify({
          type: "error",
          message:
            "There was an error completing onboarding call. Please try again.",
        });
        console.error("There was an error completing onboarding call: ", error);
      },
    }
  );

  const handleSendCompleteOnboardingCallRequest = useCallback(
    ({
      id,
      onboardingCode
    }: CompleteOnboardingCallProps) => {
      sendCompleteOnboardingCallRequestMutation.mutate({
        id,
        onboardingCode
      });
    },
    [sendCompleteOnboardingCallRequestMutation]
  );

  const value: LocationContextProps = {
    currentLocation,
    locations,
    initialLocations,
    isLocationsError,
    locationsError,
    isLocationsLoading,
    partnerId,
    locationActions: {
      create: handleCreateLocation,
      isCreateLoading: createLocationMutation.isLoading,
      update: handleUpdateLocation,
      updateConfigs: handleUpdateLocationConfigs,
      isUpdateLoading: updateLocationMutation.isLoading,
      updateSuccess: updateLocationMutation.isSuccess,
    },
    locationDetails,
    isLocationDetailsLoading,
    isLocationDetailsError,
    locationDetailsError,
    refetchLocationDetails,
    refetchLocations,
    configs,
    isLoadingConfigs,
    refetchLocationConfigs,
    sendCompleteOnboardingCallRequest: handleSendCompleteOnboardingCallRequest,
    lastOnboardingCallRequest,
    onboardingAuthUrlRequest,
    oAuthTokenRequest,
    setFilteredLocations,
    filteredLocations,
  };



  return (
    <LocationContext.Provider value={value} {...props} />
  ) as ReactElement;
}

export function useLocation(): LocationContextProps {
  const context = useContext(LocationContext);

  if (context === undefined) {
    throw new Error("useLocation must be used within a LocationProvider");
  }

  // Because we are getting the context from LocationProvider here, if context is
  // not 'undefined' (ie: the LocationProvider is not a parent of this component),
  // we can be sure context is not 'null' here
  if (context === null) {
    throw new Error("LocationProvider supplied null context");
  }



  return context;
}
